import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';

import { ConfirmationType, IParticipant } from 'APP/model/calendar/calendar.types';
import { User } from 'APP/store/Users/User/User';
import { getParticipantsCountStr } from 'APP/utils/calendar/getParticipantsCountStr';
import Entities from 'STORE';

interface IParticipantsPresenter {
  setItems(items: IParticipant[]): void;
  setItemsInfo(itemsInfo: User[]): void;
  getUserData(email: string, providerEmail: string): User | null;
  itemsInfo: User[];
  items: IParticipant[];
  noAnswerCount: number;
  maybeCount: number;
  declinedCount: number;
  acceptedCount: number;
  statuses: string;
  count: string;
  isShowInitiatorText(email: string): boolean;
}

interface IParticipantsPresenterParams {
  items: IParticipant[];
  itemsInfo: User[];
  initiatorEmail: string;
}

export const useParticipantsPresenter = (
  props: IParticipantsPresenterParams
): IParticipantsPresenter => {
  const { t } = useTranslation();

  const presenter = useLocalObservable<IParticipantsPresenter>(() => ({
    itemsInfo: [],
    items: [],

    setItems(items: IParticipant[]): void {
      presenter.items = items;
    },

    setItemsInfo(itemsInfo: User[]): void {
      presenter.itemsInfo = itemsInfo;
    },

    get acceptedCount(): number {
      return presenter.items.filter((item) => item.confirmationType === ConfirmationType.ACCEPTED)
        .length;
    },

    get declinedCount(): number {
      return presenter.items.filter((item) => item.confirmationType === ConfirmationType.DECLINED)
        .length;
    },

    get noAnswerCount(): number {
      return presenter.items.filter((item) => item.confirmationType === ConfirmationType.NO_ANSWER)
        .length;
    },

    get maybeCount(): number {
      return presenter.items.filter((item) => item.confirmationType === ConfirmationType.MAYBE)
        .length;
    },

    get statuses(): string {
      return `${t('calendar_participants_yes', {
        0: presenter.acceptedCount,
      })}, ${t('calendar_participants_maybe', {
        0: presenter.maybeCount,
      })}, ${t('calendar_participants_awaiting', { 0: presenter.noAnswerCount })}, ${t(
        'calendar_participants_no',
        {
          0: presenter.declinedCount,
        }
      )}`;
    },

    get count(): string {
      return getParticipantsCountStr(presenter.items.length);
    },

    getUserData(email: string, providerEmail: string): User | null {
      let userData = presenter.itemsInfo.find((itemInfo) => itemInfo.email === email) || null;
      const isProviderEmail = providerEmail === email;

      if (isProviderEmail && !userData) {
        userData = Entities.usersStore.getUserById(Entities.usersStore.myId!) || null;
      }

      return userData;
    },

    isShowInitiatorText(email: string): boolean {
      return props.initiatorEmail === email;
    },
  }));

  useEffect(() => {
    presenter.setItems(props.items);
  }, [props.items]);

  useEffect(() => {
    presenter.setItemsInfo(props.itemsInfo);
  }, [props.itemsInfo]);

  return presenter;
};
